/**
 * DashboardPage
 * --------------
 * Página de inicio. Para usuarios admin/dirección se presenta como pestañas:
 *   - **Resumen**: vista clásica con stats globales, presupuestos y cobros recientes.
 *   - **KPIs Dirección**: cuadro mensual con desglose por método de pago + mix
 *     de cirugías (Phase 21.1).
 *   - **Captación por origen**: de la primera valoración al quirófano, por
 *     «cómo nos conoció» (23/9/2026). La misma puerta que los KPIs: solo se
 *     pinta para admin/dirección y el servidor contesta 403 al resto.
 *
 * Para `administrativo` solo se muestra el Resumen (sin acceso económico global).
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { api, formatEUR, formatDate } from '../lib/api';
import { PageHeader, MiniStat, SectionCard, EmptyState } from '../components/Common';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  CalendarDays, FileText, Receipt, UserPlus, ArrowUpRight, ClipboardList, BarChart3, Target,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../lib/auth';
import { isManagement, isGestion } from '../lib/roles';
import MonthlyKpisTab from '../components/MonthlyKpisTab';
import CaptacionOrigenTab from '../components/CaptacionOrigenTab';
import PanelOftalmologia from '../components/oftalmo/PanelOftalmologia';
import { useClinica } from '../lib/clinica';
import { esOftalmologia } from '../lib/especialidad';

function Greeting({ name }) {
  const h = new Date().getHours();
  const part = h < 12 ? 'Buenos días' : h < 19 ? 'Buenas tardes' : 'Buenas noches';
  return `${part}, ${name || ''}`;
}

function SummaryTab({ stats, loading, mgmt, gestion }) {
  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="dashboard-kpis">
        {mgmt && (
          <MiniStat
            label="Cobros del mes"
            value={loading ? '—' : formatEUR(stats?.total_cobros_mes || 0)}
            hint={`${stats?.cobros_count_mes || 0} cobros`}
            icon={Receipt}
          />
        )}
        <MiniStat
          label="Presupuestos pendientes"
          value={loading ? '—' : (stats?.presupuestos_pendientes || 0)}
          hint={`${stats?.presupuestos_confirmados || 0} confirmados`}
          icon={FileText}
        />
        <MiniStat
          label="Pacientes nuevos"
          value={loading ? '—' : (stats?.new_patients_mes || 0)}
          hint="este mes"
          icon={UserPlus}
        />
      </div>

      {/* Presupuestos, solo a gestión (23/9/2026). La regla de dirección del
          18/8 («nadie que no sea administrativos o administradores puede ver
          presupuestos») y D2 del 22/9 («enfermería no necesita cobros»): a
          enfermería esta tarjeta le llegaba vacía diciendo «Sin presupuestos ·
          Crea tu primer presupuesto» —un vacío enseñado como dato— con un
          «Ver todos» a /presupuestos, que la echa. */}
      {gestion && (
      <div className={`grid grid-cols-1 ${mgmt ? 'lg:grid-cols-2' : ''} gap-6`}>
        <div data-testid="dashboard-presupuestos-recientes">
        <SectionCard
          title="Presupuestos recientes"
          action={<Link to="/presupuestos" className="text-sm text-[#2E6E5E] hover:underline">Ver todos</Link>}
        >
          {(!stats?.last_budgets?.length) ? (
            <EmptyState icon={FileText} title="Sin presupuestos" description="Crea tu primer presupuesto." />
          ) : (
            <div className="divide-y divide-border">
              {stats.last_budgets.map(b => (
                <div key={b.id} className="py-2.5 flex items-center justify-between text-sm">
                  <div className="min-w-0">
                    <div className="font-medium truncate">{b.patient_name}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {b.surgery_name || 'Sin cirugía'} · {formatDate(b.date)}
                    </div>
                  </div>
                  <div className="text-right">
                    {mgmt && <div className="font-semibold text-[#13231E] tabular-nums">{formatEUR(b.total)}</div>}
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{b.status}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </SectionCard>
        </div>

        {mgmt && (
          <SectionCard
            title="Cobros recientes"
            action={<Link to="/cobros" className="text-sm text-[#2E6E5E] hover:underline">Ver todos</Link>}
          >
            {(!stats?.last_cobros?.length) ? (
              <EmptyState icon={Receipt} title="Sin cobros" description="Registra un cobro para empezar." />
            ) : (
              <div className="divide-y divide-border">
                {stats.last_cobros.map(c => (
                  <div key={c.id} className="py-2.5 flex items-center justify-between text-sm">
                    <div className="min-w-0">
                      <div className="font-medium truncate">{c.patient_name}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        {c.concept} · {formatDate(c.date)} · {c.payment_method}
                      </div>
                    </div>
                    <div className="font-semibold text-[#13231E] tabular-nums">{formatEUR(c.total || c.amount)}</div>
                  </div>
                ))}
              </div>
            )}
          </SectionCard>
        )}
      </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mt-6">
        <Link to="/citas" className="cp-card p-5 group hover:border-[#2E6E5E]/40 transition-colors">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-display font-semibold text-[#13231E]">Agenda</div>
              <div className="text-xs text-muted-foreground mt-1">Consulta la agenda del día y da citas</div>
            </div>
            <ArrowUpRight className="w-5 h-5 text-[#2E6E5E]" />
          </div>
        </Link>
        <Link to="/pacientes" className="cp-card p-5 group hover:border-[#2E6E5E]/40 transition-colors">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-display font-semibold text-[#13231E]">Nuevo paciente</div>
              <div className="text-xs text-muted-foreground mt-1">Da de alta un paciente y genera su LOPD</div>
            </div>
            <ArrowUpRight className="w-5 h-5 text-[#2E6E5E]" />
          </div>
        </Link>
        <a
          href="/firma-lopd"
          target="_blank"
          rel="noreferrer"
          className="cp-card p-5 group hover:border-[#2E6E5E]/40 transition-colors"
          data-testid="dashboard-tablet-mode"
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="font-display font-semibold text-[#13231E]">Modo tablet (LOPD)</div>
              <div className="text-xs text-muted-foreground mt-1">Abre el formulario para que el paciente firme la LOPD en la tablet</div>
            </div>
            <ArrowUpRight className="w-5 h-5 text-[#2E6E5E]" />
          </div>
        </a>
        <Link to="/registro-quirurgico" className="cp-card p-5 group hover:border-[#2E6E5E]/40 transition-colors">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-display font-semibold text-[#13231E]">Registro quirúrgico</div>
              <div className="text-xs text-muted-foreground mt-1">Anota las cirugías del día y expórtalas a Excel</div>
            </div>
            <ClipboardList className="w-5 h-5 text-[#2E6E5E]" />
          </div>
        </Link>
      </div>
    </>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const mgmt = isManagement(user);
  const gestion = isGestion(user);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  // Oftalmología (4/11/2026): lo que hay que mirar hoy, lo primero.
  const oftalmologia = esOftalmologia(useClinica().clinic_profile)
    && ['admin', 'direccion', 'doctor', 'enfermeria'].includes((user?.role || '').toLowerCase());

  useEffect(() => {
    api.get('/api/dashboard/stats').then((r) => setStats(r.data)).finally(() => setLoading(false));
  }, []);

  const firstName = user?.name?.split(' ')[0] || '';

  return (
    <div>
      <PageHeader
        title={Greeting({ name: firstName })}
        subtitle={new Date().toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
        actions={
          <>
            {mgmt && (
              <Link to="/cobros">
                <Button variant="outline" data-testid="quick-new-cobro">
                  <Receipt className="w-4 h-4 mr-2" /> Nuevo cobro
                </Button>
              </Link>
            )}
            {/* Solo gestión (23/9/2026, D2 de dirección: «enfermería no
                necesita cobros»). A enfermería le llevaba a /presupuestos,
                que la echa, y el servidor le da 403 al crearlo
                (`POST /api/budgets` es de gestión). */}
            {gestion && (
              <Link to="/presupuestos">
                <Button className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="quick-new-budget">
                  <FileText className="w-4 h-4 mr-2" /> Nuevo presupuesto
                </Button>
              </Link>
            )}
          </>
        }
      />

      {oftalmologia && <PanelOftalmologia />}

      {mgmt ? (
        <Tabs defaultValue="resumen" className="mt-2" data-testid="dashboard-tabs">
          <TabsList className="grid grid-cols-3 w-full max-w-xl mb-4">
            <TabsTrigger value="resumen" data-testid="tab-resumen">
              <ClipboardList className="w-4 h-4 mr-2" /> Resumen
            </TabsTrigger>
            <TabsTrigger value="kpis" data-testid="tab-kpis">
              <BarChart3 className="w-4 h-4 mr-2" /> KPIs Dirección
            </TabsTrigger>
            <TabsTrigger value="captacion" data-testid="tab-captacion">
              <Target className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Captación por origen</span>
              <span className="sm:hidden">Captación</span>
            </TabsTrigger>
          </TabsList>
          <TabsContent value="resumen">
            <SummaryTab stats={stats} loading={loading} mgmt={mgmt} gestion={gestion} />
          </TabsContent>
          <TabsContent value="kpis">
            <MonthlyKpisTab />
          </TabsContent>
          <TabsContent value="captacion">
            <CaptacionOrigenTab />
          </TabsContent>
        </Tabs>
      ) : (
        <SummaryTab stats={stats} loading={loading} mgmt={mgmt} gestion={gestion} />
      )}
    </div>
  );
}
